import { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function TosModal() {
  const { user, reload } = useAuth();
  const [busy, setBusy] = useState(false);

  // Only render when we have an authenticated user who hasn't accepted yet
  if (!user || user === false) return null;
  if (user.tos_accepted_at) return null;

  const accept = async () => {
    setBusy(true);
    try {
      await api.post("/tos/accept");
      await reload();
      toast.success("Thanks — you're all set");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't record acceptance");
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="tos-modal-overlay"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4 py-6"
      // Deliberately no click-outside-to-close; user must click Agree.
      role="dialog"
      aria-modal="true"
      aria-labelledby="tos-title"
    >
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl flex flex-col max-h-[90vh]" data-testid="tos-modal">
        <div className="px-6 pt-6 pb-4 border-b border-[#EBE8E0]">
          <div className="flex items-center gap-2">
            <ShieldCheck size={22} weight="duotone" className="text-[#2C5545]" />
            <h2 id="tos-title" className="font-heading text-2xl font-bold" data-testid="tos-title">
              Terms of Service
            </h2>
          </div>
          <p className="text-sm text-[#576A62] mt-2">
            Please review and accept before continuing. You cannot use EverCara until you agree.
          </p>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6 text-[15px] leading-relaxed text-[#1A2520]" data-testid="tos-body">
          <section>
            <h3 className="font-heading font-bold text-lg mb-2">3. ASSUMPTION OF RISK AND NO CLINICAL MONITORING</h3>
            <p>
              You acknowledge and agree that any information entered into the App—including but not
              limited to medication schedules, dosage compliance, blood pressure readings, glucose
              metrics, or symptom records—is managed and inputted entirely at your own risk. Evercara
              does not review, verify, audit, or monitor your entries for medical accuracy, safety,
              or drug interactions.
            </p>
            <p className="mt-3">
              While the App includes reminder and notification functions (such as pill schedules and
              vital log alerts), you acknowledge that these features rely on user settings, device
              connectivity, and software performance. Evercara does not guarantee that reminders will
              occur without interruption or delay, and you retain sole responsibility for ensuring
              that medications and medical care are administered correctly and safely.
            </p>
          </section>

          <section>
            <h3 className="font-heading font-bold text-lg mb-2">4. ASSISTANCE LIMITATION OF LIABILITY</h3>
            <p>
              To the maximum extent permitted by applicable law, Evercara, its creator(s), and its
              affiliates shall not be held liable for any direct, indirect, incidental,
              consequential, or punitive damages arising out of your use of, or inability to use,
              the App. This includes, without limitation, any claims related to missed medication
              doses, data logging errors, server connectivity drops, or reliance on information
              stored within the App. Always double-check physical prescription labels and consult
              directly with your primary care team regarding all medical routines.
            </p>
          </section>

          <div className="flex items-start gap-2 rounded-xl bg-[#D49A4C]/12 border border-[#D49A4C]/40 p-4 text-sm text-[#B37927]">
            <Warning size={18} weight="duotone" className="shrink-0 mt-0.5" />
            <div>
              EverCara is a personal organizer for care, not medical software. It does not
              provide medical advice, diagnosis, or treatment. In an emergency, call your
              local emergency number.
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-[#EBE8E0] bg-[#FDFBF7] rounded-b-2xl">
          <button
            onClick={accept}
            disabled={busy}
            data-testid="tos-agree-btn"
            className="w-full h-12 rounded-full bg-[#2C5545] hover:bg-[#224235] disabled:opacity-60 text-white font-semibold transition-colors"
          >
            {busy ? "Recording…" : "Agree and continue"}
          </button>
          <p className="text-xs text-[#576A62] text-center mt-3">
            By clicking Agree, you acknowledge you have read and accept the Terms above.
          </p>
        </div>
      </div>
    </div>
  );
}
